import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '../../../lib/api';
import { toast } from 'sonner';
import { Briefcase, Plus, X, Users, MapPin, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600 border-slate-300',
  open: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  filled: 'bg-teal-50 text-teal-700 border-teal-200',
  closed: 'bg-red-50 text-red-700 border-red-200',
};

const DEPARTMENTS = ['Projects/Site', 'Procurement', 'Engineering', 'HR & Admin', 'Finance & Accounts', 'Business Development', 'Quality', 'Safety/HSE', 'Stores', 'IT', 'Management'];

const blank = { title: '', department: '', grade: '', headcount: 1, jd_summary: '', location: '', closing_date: '', max_ctc: '', min_experience_years: '', status: 'open', source: 'external' };

export default function JobOpenings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const role = (user?.role || '').toLowerCase();
  const dept = (user?.department || '').toUpperCase();
  const canWrite = ['super_admin', 'admin', 'hr', 'md'].includes(role) || dept === 'HR' || dept === 'MD';

  const load = () => apiGet('/hr/jobs', filter ? { status: filter } : undefined)
    .then((r) => setJobs(r.items || [])).catch(() => toast.error('Failed to load jobs'));
  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Job title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form,
        headcount: Number(form.headcount) || 1,
        max_ctc: form.max_ctc ? Number(form.max_ctc) : null,
        min_experience_years: form.min_experience_years ? Number(form.min_experience_years) : null,
      };
      const created = await apiPost('/hr/jobs', payload);
      toast.success('Job opening created');
      setShowForm(false); setForm(blank);
      nav(`/app/hr/recruitment/jobs/${created.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5 p-6 md:p-8" data-testid="job-openings-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">Recruitment</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Job Openings</h1>
          <p className="text-sm text-slate-600 mt-1">Create roles and track candidates through the hiring pipeline.</p>
        </div>
        {canWrite && (
          <button onClick={() => { setForm(blank); setShowForm(true); }} data-testid="new-job-btn"
            className="px-4 py-2 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] flex items-center gap-2">
            <Plus size={14} /> New Opening
          </button>
        )}
      </div>

      <div className="flex gap-2">
        {['', 'open', 'draft', 'filled', 'closed'].map((s) => (
          <button key={s || 'all'} onClick={() => setFilter(s)} data-testid={`filter-${s || 'all'}`}
            className={`px-3 py-1.5 rounded-sm text-[11px] font-display font-bold uppercase tracking-wider border ${filter === s ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-white text-slate-600 border-slate-200'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {jobs.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="jobs-empty">
          <Briefcase size={40} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">No job openings yet.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-grid">
          {jobs.map((j) => (
            <button key={j.id} onClick={() => nav(`/app/hr/recruitment/jobs/${j.id}`)} data-testid={`job-card-${j.id}`}
              className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-bold text-sm">{j.title}</div>
                <span className={`text-[9px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${STATUS_COLORS[j.status] || STATUS_COLORS.draft}`}>{j.status}</span>
              </div>
              <div className="text-[11px] text-slate-500 mt-1">{j.department || '—'}{j.grade ? ` · ${j.grade}` : ''}</div>
              <div className="flex items-center gap-3 mt-3 text-[11px] text-slate-600">
                <span className="flex items-center gap-1"><Users size={11} /> {j.candidate_count || 0}</span>
                <span className="flex items-center gap-1"><CheckCircle2 size={11} /> {j.headcount} open</span>
                {j.location && <span className="flex items-center gap-1"><MapPin size={11} /> {j.location}</span>}
              </div>
              {j.closing_date && <div className="text-[10px] text-amber-600 mt-2">Closes {j.closing_date}</div>}
            </button>
          ))}
        </div>
      )}

      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowForm(false)} />
          <div className="fixed inset-y-0 right-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col" data-testid="job-form-drawer">
            <header className="px-5 py-4 border-b flex items-center justify-between bg-[#1F7A6E] text-white">
              <h2 className="font-display font-bold tracking-wide uppercase text-sm">New Job Opening</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </header>
            <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-3">
              <Field label="Job Title *"><input data-testid="job-title-input" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
              <Field label="Department">
                <select data-testid="job-dept-select" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                  <option value="">Select…</option>
                  {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Grade"><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} /></Field>
                <Field label="Headcount"><input type="number" min="1" data-testid="job-headcount-input" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Min Experience (yrs)"><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.min_experience_years} onChange={(e) => setForm({ ...form, min_experience_years: e.target.value })} /></Field>
                <Field label="Max CTC (₹/yr)"><input type="number" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.max_ctc} onChange={(e) => setForm({ ...form, max_ctc: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Location"><input className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></Field>
                <Field label="Closing Date"><input type="date" className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.closing_date} onChange={(e) => setForm({ ...form, closing_date: e.target.value })} /></Field>
              </div>
              <Field label="JD Summary"><textarea rows={4} className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.jd_summary} onChange={(e) => setForm({ ...form, jd_summary: e.target.value })} /></Field>
              <Field label="Status">
                <select className="w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#1F7A6E]" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="open">Open</option><option value="draft">Draft</option>
                </select>
              </Field>
              <button type="submit" disabled={saving} data-testid="job-submit-btn"
                className="w-full py-2.5 bg-[#1F7A6E] text-white rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-[#2e3a5c] disabled:opacity-50">
                {saving ? 'Creating…' : 'Create Opening'}
              </button>
            </form>
          </div>
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
